import Switch from '../ui/Switch'
import { safeJsonObject } from '../lib/safeJson'
import { useState } from 'react'

import type { ColumnInfo } from '../api/client'

/**
 * 进度列设置（M9.8 / backlog #8 的 UI 部分）。
 *
 * 用户 2026-10-20 确认的规则：
 *  · 权重开关**默认关闭**，关闭时等权平均；
 *  · 开启后才显示每列权重；
 *  · 没有值的列整列跳过；有值但标签没配完成度按 0 计（这两条在计算层，不在这里管）。
 *
 * 存到哪里：**进度列自己的 settings**（即数据库里的 BoardColumn.settings），不是前端存储。
 */

interface SettingsShape {
  weightsEnabled?: boolean
  sources?: { column?: string; weight?: number }[]
}

function readSettings(col: ColumnInfo): SettingsShape {
  return safeJsonObject<SettingsShape>(col.settings, {}, 'progressSettings')
}

/** 有资格参与进度统计的 status 列：settings 里出现了完成度配置 */
export function progressSourceColumns(columns: ColumnInfo[]): ColumnInfo[] {
  return columns.filter((c) => {
    if (c.type !== 'color') return false
    const s = readSettings(c) as Record<string, unknown>
    const lp = s.label_progress
    const lpm = s.label_progress_mode
    const dc = s.done_colors
    return (
      (lp !== null && typeof lp === 'object' && Object.keys(lp as object).length > 0) ||
      (lpm !== null && typeof lpm === 'object' && Object.keys(lpm as object).length > 0) ||
      (Array.isArray(dc) && dc.length > 0)
    )
  })
}

export interface ProgressSettingsProps {
  column: ColumnInfo
  columns: ColumnInfo[]
  busy: boolean
  onCancel: () => void
  onSave: (settingsJson: string) => void
}

export default function ProgressSettings({ column, columns, busy, onCancel, onSave }: ProgressSettingsProps) {
  const initial = readSettings(column)
  const candidates = progressSourceColumns(columns)
  const [enabled, setEnabled] = useState(initial.weightsEnabled === true)
  const [weights, setWeights] = useState<Record<string, number>>(() => {
    const out: Record<string, number> = {}
    for (const c of candidates) out[c.key] = 1
    for (const s of initial.sources ?? []) {
      if (typeof s.column === 'string' && typeof s.weight === 'number') out[s.column] = s.weight
    }
    return out
  })

  const save = () => {
    const sources = candidates.map((c) => ({ column: c.key, weight: weights[c.key] ?? 1 }))
    onSave(JSON.stringify({ weightsEnabled: enabled, sources }))
  }

  return (
    <div className="dialog-mask" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="dialog formula-editor" onClick={(e) => e.stopPropagation()}>
        <div className="dialog__title">设置进度 —— {column.title}</div>
        <div className="formula-body">
          {candidates.length === 0 && (
            <div className="formula-hint formula-hint--bad">
              看板里还没有「配置了完成度」的状态列。请先在状态列的标签里设置完成/百分比。
            </div>
          )}
          <div className="progress-switch">
            <Switch checked={enabled} onChange={setEnabled} label="按权重计算" />
            <span>按权重计算（关闭时按等权平均）</span>
          </div>
          {enabled && candidates.length > 0 && (
            <div className="progress-weights">
              {candidates.map((c) => (
                <label key={c.key} className="progress-weight">
                  <span className="progress-weight__name">{c.title}</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={weights[c.key] ?? 1}
                    onChange={(e) => setWeights((prev) => ({ ...prev, [c.key]: Number(e.target.value) }))}
                  />
                </label>
              ))}
            </div>
          )}
          <div className="formula-help">
            <div>规则：某个条目在某列没有值时整列跳过；有值但该标签没配完成度，按 0 计。</div>
            <div>设置保存在这条进度列的 settings 里（数据库），不是浏览器本地存储。</div>
          </div>
        </div>
        <div className="dialog__actions">
          <button type="button" className="dialog__btn" disabled={busy} onClick={onCancel}>取消</button>
          <button type="button" className="dialog__btn dialog__btn--primary" disabled={busy} onClick={save}>保存</button>
        </div>
      </div>
    </div>
  )
}
